import React from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Text,
  Card,
} from '@fluentui/react-components';
import {
  ChartMultiple24Regular,
  DocumentTable24Regular,
  Money24Regular,
  People24Regular,
  Box24Regular,
  Calendar24Regular,
  DataTrending24Regular,
  DataUsage24Regular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  container: {
    ...shorthands.padding('24px'),
    backgroundColor: tokens.colorNeutralBackground3,
    minHeight: '100vh',
  },
  header: {
    marginBottom: '32px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '20px',
  },
  reportCard: {
    cursor: 'pointer',
    ...shorthands.padding('24px'),
    ...shorthands.transition('all', '0.2s', 'ease'),
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: tokens.shadow16,
    },
  },
  iconContainer: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    backgroundColor: tokens.colorBrandBackground2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  icon: {
    fontSize: '24px',
    color: tokens.colorBrandForeground1,
    width: '24px',
    height: '24px',
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '8px',
  },
  cardDescription: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
    lineHeight: '1.5',
  },
});

interface ReportItem {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType;
}

const reportCategories: ReportItem[] = [
  {
    id: 'sales',
    title: 'Sales Reports',
    description: 'View sales performance, trends, and revenue analytics',
    icon: ChartMultiple24Regular,
  },
  {
    id: 'orders',
    title: 'Order Reports',
    description: 'Analyze order history, status, and fulfillment metrics',
    icon: DocumentTable24Regular,
  },
  {
    id: 'financial',
    title: 'Financial Reports',
    description: 'Review income statements, expenses, and profit margins',
    icon: Money24Regular,
  },
  {
    id: 'customers',
    title: 'Customer Reports',
    description: 'Customer analytics, retention rates, and demographics',
    icon: People24Regular,
  },
  {
    id: 'inventory',
    title: 'Inventory Reports',
    description: 'Stock levels, utilization rates, and inventory turnover',
    icon: Box24Regular,
  },
  {
    id: 'calendar',
    title: 'Calendar Reports',
    description: 'Scheduling analytics and resource utilization',
    icon: Calendar24Regular,
  },
  {
    id: 'trends',
    title: 'Trend Analysis',
    description: 'Historical data trends and predictive analytics',
    icon: DataTrending24Regular,
  },
  {
    id: 'performance',
    title: 'Performance Metrics',
    description: 'KPIs, benchmarks, and operational efficiency',
    icon: DataUsage24Regular,
  },
];

export default function Reports() {
  const styles = useStyles();

  const handleReportClick = (reportId: string) => {
    console.log('Opening report:', reportId);
    // TODO: Navigate to specific report or open report modal
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text className={styles.title}>Reports Dashboard</Text>
        <Text className={styles.subtitle}>
          Access comprehensive reports and analytics for your business
        </Text>
      </div>

      <div className={styles.grid}>
        {reportCategories.map((report) => {
          return (
            <Card
              key={report.id}
              className={styles.reportCard}
              onClick={() => handleReportClick(report.id)}
            >
              <div className={styles.iconContainer}>
              </div>
              <Text className={styles.cardTitle}>{report.title}</Text>
              <Text className={styles.cardDescription}>{report.description}</Text>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
